import React from 'react';
import { T, useGT } from 'gt-react';
import { toast } from 'sonner';
import { builtInDefaultColumnMappings } from '@erp/rpc';
import { PencilSimpleIcon, SpinnerIcon } from '@phosphor-icons/react';
import { Button } from 'src/components/ui/button';
import {
  useCreateSd844DefaultFromBuiltIn,
  useSd844TemplateSampleValues,
} from 'src/api/rpc/hooks/ship-and-debit/useSd844EmailTemplates';
import { Sd844TemplateColumnMapper } from './Sd844TemplateColumnMapper';

/**
 * The default 844 file as it is built in code today — shown read-only, so the
 * default page is never blank, with one click to turn it into an editable org
 * default. Nothing changes for any supplier until that click: the saved copy
 * is generated from the same code, column for column.
 */

/** Generated once: the bindings are a pure function of the code's layout. */
const BUILT_IN_MAPPINGS = builtInDefaultColumnMappings();

function noop() {}

export function Sd844BuiltInDefaultPreview({ canEdit }: { canEdit: boolean }) {
  const gt = useGT();
  const create = useCreateSd844DefaultFromBuiltIn();
  const sample = useSd844TemplateSampleValues({
    companyId: null,
    sample: 'staged',
    columnMappings: BUILT_IN_MAPPINGS,
    enabled: true,
  });
  const sampleValues = new Map(
    (sample.data?.values ?? []).map((v) => [v.columnIndex, v.value]),
  );

  async function customize() {
    try {
      await create.mutateAsync({});
      toast.success(
        gt('Default template created. You can now edit it.', {
          context: 'toast after creating the editable default 844 template',
        }),
      );
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : gt('The default template could not be created.', {
              context: 'error creating the default 844 template',
            }),
      );
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-muted-foreground text-xs">
          <T context="explains the read-only built-in default 844 template">
            This is the built-in default — 35 columns for immediate claims, with
            VDM # added to deferred claims. Click{' '}
            <strong>Customize Default</strong> to make an editable copy; it
            starts identical, so nothing changes for suppliers until you edit
            and save it.
          </T>
        </p>
        {canEdit && (
          <Button
            type="button"
            size="sm"
            onClick={() => void customize()}
            disabled={create.isPending}
          >
            {create.isPending ? (
              <SpinnerIcon className="size-4 animate-spin" />
            ) : (
              <PencilSimpleIcon className="size-4" />
            )}
            <T context="button that creates an editable copy of the built-in default 844 template">
              Customize Default
            </T>
          </Button>
        )}
      </div>
      <Sd844TemplateColumnMapper
        mappings={BUILT_IN_MAPPINGS}
        onChangeSource={noop}
        sampleValues={sampleValues}
        hasSample={!!sample.data?.sample}
        readOnly
      />
    </div>
  );
}
